import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { getCourses } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, Users, Clock, Filter, Grid, List, ArrowRight, Palette, Box, Wand2, Scissors, GraduationCap, Cpu, IndianRupee } from 'lucide-react';

// Course styling metadata (icons, colors, badges)
const courseStyles = {
  'VIA001': { icon: Palette, color: 'from-emerald-500 to-teal-500', badge: 'badge-green' },
  'VIA002': { icon: Box, color: 'from-blue-500 to-cyan-500', badge: 'badge-blue' },
  'VIA003': { icon: Wand2, color: 'from-purple-500 to-pink-500', badge: 'badge-purple' },
  'VIA004': { icon: Scissors, color: 'from-orange-500 to-amber-500', badge: 'badge-orange' },
  'VIA006': { icon: Cpu, color: 'from-violet-500 to-purple-500', badge: 'badge-violet' },
  'VIA007': { icon: Palette, color: 'from-pink-500 to-rose-500', badge: 'badge-pink' },
  'VIA008': { icon: Scissors, color: 'from-amber-500 to-yellow-500', badge: 'badge-amber' },
  'VIA_GRD_001': { icon: GraduationCap, color: 'from-red-500 to-rose-500', badge: 'badge-red' },
  'VIA_CRSH_001': { icon: Palette, color: 'from-lime-500 to-green-500', badge: 'badge-green' },
  'VIA_CRSH_002': { icon: Cpu, color: 'from-sky-500 to-blue-500', badge: 'badge-blue' },
  'VIA_CRSH_003': { icon: Box, color: 'from-indigo-500 to-violet-500', badge: 'badge-violet' },
  'default': { icon: Palette, color: 'from-gray-500 to-slate-500', badge: 'badge-gray' }
};

export default function Courses() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [viewMode, setViewMode] = useState('grid');

  // Derive categories dynamically from loaded courses
  const categories = ['All Programs', ...Array.from(new Set(courses.map(c => c.category).filter(Boolean)))];

  // Fetch courses from API
  useEffect(() => {
    loadCourses();
  }, []);

  // Filter courses when search/category changes
  useEffect(() => {
    filterCourses();
  }, [category, search, courses]);

  const loadCourses = async () => {
    setLoading(true);
    try {
      const response = await getCourses();
      // Filter to only show published courses
      const publishedCourses = (response.data || []).filter(c => c.status === 'published' || c.is_published);
      setCourses(publishedCourses);
    } catch (error) {
      console.error('Error loading courses:', error);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const filterCourses = () => {
    let filtered = [...courses];
    
    if (category && category !== 'All Programs') {
      filtered = filtered.filter(c => c.category === category);
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(c => 
        c.title?.toLowerCase().includes(searchLower) ||
        c.description?.toLowerCase().includes(searchLower) ||
        c.course_id?.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredCourses(filtered);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category && category !== 'All Programs') params.set('category', category);
    setSearchParams(params);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDuration = (course) => {
    if (course.duration_months) {
      return `${course.duration_months} Month${course.duration_months > 1 ? 's' : ''}`;
    }
    const weeks = course.duration_weeks || 12;
    if (weeks >= 52) {
      const years = Math.floor(weeks / 52);
      return `${years} Year${years > 1 ? 's' : ''}`;
    }
    return `${weeks} Weeks`;
  };

  // Get the best price to display (lowest tier price or base price)
  const getDisplayPrice = (course) => {
    if (course.pricing_tiers) {
      const selfLearningPrice = course.pricing_tiers.self_learning?.enabled !== false 
        ? course.pricing_tiers.self_learning?.price 
        : null;
      const instructorLedPrice = course.pricing_tiers.instructor_led?.enabled !== false 
        ? course.pricing_tiers.instructor_led?.price 
        : null;
      
      // Return the lowest enabled price
      if (selfLearningPrice && instructorLedPrice) {
        return { price: Math.min(selfLearningPrice, instructorLedPrice), hasMultipleTiers: true };
      }
      if (selfLearningPrice) return { price: selfLearningPrice, hasMultipleTiers: false };
      if (instructorLedPrice) return { price: instructorLedPrice, hasMultipleTiers: false };
    }
    return { price: course.price || 0, hasMultipleTiers: false };
  };

  // Get course style (icon, color, badge)
  const getCourseStyle = (courseId) => {
    return courseStyles[courseId] || courseStyles['default'];
  };

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <Badge variant="outline" className="mb-4 bg-emerald-500/10 text-emerald-500 border-emerald-500/30">
            Industry-Ready Programs
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Transform Your <span className="text-gradient-green">Creative Career</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Choose from our curated programs designed by industry experts. 
            Both self-paced and instructor-led options available.
          </p>
        </motion.div>

        {/* Search & Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <Card className="card-vibrant">
            <CardContent className="p-4">
              <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    placeholder="Search programs..."
                    className="pl-10 bg-background"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Select value={category || 'All Programs'} onValueChange={setCategory}>
                    <SelectTrigger className="w-48 bg-background">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="hidden md:flex gap-1 border rounded-lg p-1">
                    <Button
                      variant={viewMode === 'grid' ? 'default' : 'ghost'}
                      size="icon"
                      onClick={() => setViewMode('grid')}
                      className="h-8 w-8"
                    >
                      <Grid className="w-4 h-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'default' : 'ghost'}
                      size="icon"
                      onClick={() => setViewMode('list')}
                      className="h-8 w-8"
                    >
                      <List className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Course Grid */}
        {loading ? (
          <div className={`grid ${viewMode === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3' : ''} gap-6`}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="card-vibrant">
                <Skeleton className="aspect-video" />
                <CardContent className="p-5">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full mb-4" />
                  <Skeleton className="h-4 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className={`grid ${viewMode === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3' : ''} gap-8`}>
            {filteredCourses.map((course, i) => {
              const style = getCourseStyle(course.course_id);
              const IconComponent = style.icon;
              const { price, hasMultipleTiers } = getDisplayPrice(course);
              
              return (
                <motion.div
                  key={course.course_id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card
                    className={`card-vibrant cursor-pointer bg-card overflow-hidden ${
                      viewMode === 'list' ? 'flex' : ''
                    }`}
                    onClick={() => navigate(`/courses/${course.course_id}`)}
                    data-testid={`course-${course.course_id}`}
                  >
                    <div className={`relative ${viewMode === 'list' ? 'w-64 flex-shrink-0' : 'aspect-video'}`}>
                      <img
                        src={course.thumbnail || 'https://images.unsplash.com/photo-1561070791-2526d30994b5'}
                        alt={course.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1561070791-2526d30994b5';
                        }}
                      />
                      <div className={`absolute inset-0 bg-gradient-to-t ${style.color} opacity-60`} />
                      <Badge className={`absolute top-4 left-4 ${style.badge} font-mono`}>
                        {course.course_id}
                      </Badge>
                      {hasMultipleTiers && (
                        <Badge className="absolute top-4 right-4 bg-amber-500 text-white">
                          2 Plans
                        </Badge>
                      )}
                      <div className="absolute bottom-4 left-4 flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-white font-semibold text-sm">
                          {formatDuration(course)}
                        </span>
                      </div>
                    </div>
                    <CardContent className={`p-6 ${viewMode === 'list' ? 'flex-1 flex flex-col justify-between' : ''}`}>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-xs">{course.category}</Badge>
                        </div>
                        <h3 className="font-bold text-xl mb-2 line-clamp-2">{course.title}</h3>
                        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                          {course.description}
                        </p>
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Users className="w-4 h-4" />
                          <span>{course.enrolled_count || 0} students</span>
                        </div>
                        <div className="text-right">
                          {hasMultipleTiers && (
                            <span className="text-xs text-muted-foreground block">Starting from</span>
                          )}
                          <span className="font-bold text-lg text-gradient-green flex items-center">
                            <IndianRupee className="w-4 h-4" />
                            {price.toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>
                      <Button 
                        className="w-full mt-4 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white hover:opacity-90"
                      >
                        View Program <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {!loading && filteredCourses.length === 0 && (
          <div className="text-center py-16">
            <Search className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-2xl font-bold mb-2">No programs found</h3>
            <p className="text-muted-foreground mb-6">
              Try adjusting your search or filter criteria
            </p>
            <Button 
              onClick={() => { setSearch(''); setCategory(''); }}
              className="rounded-full"
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
